import * as React from "react";

import { Release } from "../../../../../../../../common/types/release/release";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import ListItemText from '@material-ui/core/ListItemText';

import { capitalize } from "../../../../../utils/capitalize";
import { getReleaseDate } from "../../../../../utils/get-release-date";
import { getPreorderDate } from "../../../../../utils/get-preorder-date";
import { createMuiTheme, OutlinedInput, PaletteType, ThemeProvider } from "@material-ui/core";
import SimpleTrack from "../../../../../../../../common/types/release/simple-track";
import styled from 'styled-components';
import { getGenres } from "../../../../../sdk/categories";
import { Category } from '../../../../../../../../common/types/categories/category';
import { ReleaseGenre } from "../../../../../../../../common/types/release/release-genre";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ReleaseDetailArtists } from "../../detail-affiliation/detail-artists";

interface ReleaseDetailSummaryProps {
  release: Release;
  tracks: SimpleTrack[];
  onUpdateField: <T extends keyof Release>(key: T, value: Release[T]) => void;
}

const theme = createMuiTheme({
  palette: {
    type: "dark" as PaletteType,
    primary: {
      main: "#999",
    },
  },
});

const sortPopular = (a: Category<ReleaseGenre>, b: Category<ReleaseGenre>) => {
  if (a.metadata.isPopular === b.metadata.isPopular) {
    return a.name.localeCompare(b.name);
  }

  if (a.metadata.isPopular) {
    return -1;
  }

  return 1;
};

export const ReleaseDetailSummary: React.FC<ReleaseDetailSummaryProps> = (
  props
) => {
  const { release, tracks } = props;
  const [genres, setGenres] = React.useState<Category<any>[]>([]);
  const [releaseGenres, setReleaseGenres] = React.useState<string[]>([]);

  const getVersion = () => {
    if (release.version?.toUpperCase() === "ORIGINAL") {
      return "";
    }

    return release.version || "";
  };

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    if (Array.isArray(value) && value.length === 0){
      return;
    }
    setReleaseGenres(
      typeof value === 'string' ? value.split(',') : value,
    );
    let newList: ReleaseGenre[] = [];
    const items = [];
    for (const strValue of value) {
      const searchItem = genres.find((a) => a.metadata?.value === strValue);
      if (searchItem?.metadata) {
        items.push(searchItem?.metadata)
      }
    }

    for (const item of items) {
      newList.push(item);
    }

    if (newList.length > 2) {
      newList = newList.filter((_, i) => i !== 1);
    }
    props.onUpdateField("genres", newList as ReleaseGenre[]);
  };

  React.useEffect(() => {
    getGenres().then((response) => {
      setGenres(response.sort(sortPopular));
    })
  }, []);

  React.useEffect(() => {
    setReleaseGenres(release.genres.map((g) => g.value));
  }, [release]);

  const getList = () => {
    const partial = genres;
    const missing = release.genres.filter(
      (a) => !partial.find((b) => a.value === b.metadata?.value)
    );
    const selected = missing.map((g) =>
      genres.find((o) => o.metadata?.value === g.value)
    );

    return partial.concat(
      selected.filter((o) => o !== undefined) as Category<ReleaseGenre>[]
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container spacing={3}>
        <Grid item xs={9}>
          <TextField
            fullWidth
            label="Title"
            value={release.title}
            onChange={(ev) => props.onUpdateField("title", ev.target.value)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Version"
            value={getVersion()}
            onChange={(ev) => props.onUpdateField("version", ev.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <ReleaseDetailArtists onUpdateField={props.onUpdateField} artists={release.artists}></ReleaseDetailArtists>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label="Type"
                value={capitalize(release.type)}
                onChange={() => null}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel id="genre-label" style={{fontSize: "smaller"}}>Genres</InputLabel>
              <Select
                style={{width: "100%", height: '38px'}}
                labelId="genre-label"
                multiple
                value={releaseGenres}
                onChange={handleChange}
                input={<OutlinedInput label="Tag" />}
                renderValue={(selected) => (selected as unknown as string[]).join(', ')}
              >
                {getList().map((item) => (
                  <MenuItem key={item.name} value={item.name}>
                    <Checkbox checked={releaseGenres.indexOf(item.name) > -1} />
                    <ListItemText primary={item.name} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label="Language"
                value={release.metadataLanguage || ""}
                onChange={(ev) =>
                  props.onUpdateField("metadataLanguage", ev.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label="UPC"
                value={release.UPCEAN}
                onChange={(ev) =>
                  props.onUpdateField("UPCEAN", ev.target.value)
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Release History"
                value={
                  release.releaseHistory === "previous"
                    ? "Previous Release"
                    : "New Release"
                }
                onChange={() => null}
              />
            </Grid>
            {release.releaseHistory === "previous" && (
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Previous Release Date"
                value={getReleaseDate(release, 'previousReleaseDate')}
                // onChange={(ev) => props.onUpdateField('previousReleaseDate', serializeDateField(ev.target.value))}
                onChange={() => null}
              />
            </Grid>
            )}
            <Grid item xs={3}>
              <DateWrapper>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    disableToolbar
                    variant="inline"
                    label="Releease date"
                    value={ new Date(release.releaseDate || '')}
                    onChange={(ev) => props.onUpdateField('releaseDate', ev?.toUTCString())}
                  />
                </MuiPickersUtilsProvider>
              </DateWrapper>
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Pre-order Date"
                value={getPreorderDate(release)}
                onChange={() => null}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Tracks"
                value={release.trackCount}
                onChange={() => null}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Owners"
                value={release.ownerCount}
                onChange={() => null}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      release.isExplicit || tracks.some((t) => t.isExplicit)
                    }
                    onChange={() => null}
                    color="default"
                  />
                }
                label="Explicit"
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={release.isInstrumental}
                    onChange={() => null}
                    color="default"
                  />
                }
                label="Instrumental"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="select-label-stores">Stores</InputLabel>
                <Select
                  labelId="select-label-stores"
                  id="select-stores"
                  placeholder={release.storesBrief}
                  multiple
                  fullWidth
                  value={release.stores.map((s) => s.name)}
                  onChange={() => null}
                >
                  {release.stores.map((store) => (
                    <MenuItem
                      key={`select-stores-${store.id}`}
                      value={store.name}
                    >
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel shrink id="select-label-regions">
                  Regions
                </InputLabel>
                <Select
                  labelId="select-label-regions"
                  id="select-regions"
                  multiple
                  fullWidth
                  displayEmpty
                  value={
                    (release.regions?.map((s) => s.name) as string[]) || []
                  }
                  onChange={() => null}
                  renderValue={(selected: any) => {
                    if (selected.length === 0) {
                      return "Worldwide";
                    }
                    return selected.join(", ");
                  }}
                >
                  <MenuItem value="">
                    <em>Worldwide</em>
                  </MenuItem>
                  {release.regions?.map((region) => (
                    <MenuItem
                      key={`select-regions-${region.id}`}
                      value={region.name}
                    >
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={release.hasYoutubeContentID}
                    onChange={() =>
                      props.onUpdateField("hasYoutubeContentID", !release.hasYoutubeContentID)
                    }
                    color="secondary"
                  />
                }
                label="Has Content ID"
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={release.tosAccepted}
                    onChange={() => null}
                    color="default"
                  />
                }
                label="ToS Accepted"
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={release.recordedAccepted}
                    onChange={() => null}
                    color="default"
                  />
                }
                label="Record Rights"
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={release.authorAccepted}
                    onChange={() => null}
                    color="default"
                  />
                }
                label="Is Author"
              />
            </Grid>
          </Grid>
        </Grid>

        {release.backofficeReasons ? (
          <Grid item xs={12}>
            <TextField
              label="Last Message"
              value={release.backofficeReasons || ""}
              multiline
              fullWidth
              rowsMax={24}
            />
          </Grid>
        ) : null}
      </Grid>
    </ThemeProvider>
  );
};

const DateWrapper = styled.div`
  max-width: 8rem !important;
  margin-left: 1rem;
  margin-right: 1rem;
`;
